// Data Management: how data gets in ("bisa pakai integrasi API atau upload
// data manual") and how healthy what is loaded is. Status, issues, and the
// expected file format all come from the API; the upload goes to
// POST /api/i/:slug/dataset/upload and its result is shown in full, so a
// reader knows exactly what to fix. API integration has no backend yet and
// says so, with no button that does nothing.
//
// Order, owner's call (2026-10-05): the two ways data gets in come first,
// upload then API integration, because that is what a person opens this page
// to do. Then the dataset note, health (four tiles, issues), the format, and
// the loaded tables in one flush panel grouped by kind. JEV ui.final_taste
// keeps upload and API integration as a stack, not halves, so the API panel
// never floats in a stretched box.
// The upload controls share the FileDrop column (.con-drop-field, the kit
// form cap), so the select, the drop zone, and the result keep one right edge.
import { useId, useState, type ReactNode } from "react";
import {
  Badge,
  DataTable,
  EmptyState,
  FileDrop,
  ListRows,
  Notice,
  PageHeader,
  Panel,
  StatTile,
  StatTileRow,
  type DataColumn,
  type FileDropState,
} from "@dashboard-intelligent/ui";
import type {
  DataIssue,
  DatasetSchema,
  DatasetStatus,
  IndustrySlug,
  IngestResult,
  TableStatus,
} from "@dashboard-intelligent/api/src/modules/industry/types";
import { ApiError, endpoints } from "../endpoints";
import { Ident, rawText } from "./shared/raw";
import { useIndustry } from "../industry";
import { useApi, type ApiState } from "../useApi";
import { formatMetric } from "../format";
import {
  KIND_TEXT,
  SEVERITY,
  acceptAttr,
  acceptsText,
  bytesText,
  groupByKind,
  isSingleTableFile,
  loadedAtText,
  matchesAccept,
  needsTableChoice,
  share,
  sortIssues,
} from "./data/dataset";

const ORIGIN_TEXT: Record<DatasetStatus["origin"], string> = {
  bundled: "bawaan aplikasi",
  upload: "hasil unggahan",
};

const count = (n: number) => formatMetric(n, "count");

export function DataManagementPage() {
  const { slug, summary } = useIndustry();
  const status = useApi(() => endpoints.datasetStatus(slug));
  const schema = useApi(() => endpoints.datasetSchema(slug));

  return (
    <>
      <PageHeader
        title="Data Management"
        description="Muat data lewat unggahan manual atau integrasi API, lalu periksa apa yang dimuat dan seberapa sehat datanya."
      />
      <UploadPanel slug={slug} schema={schema} onUploaded={status.reload} />
      <Panel title="Integrasi API" description="Tarik data otomatis dari sistem sumber.">
        <EmptyState icon="data" title="Belum terhubung">
          Server belum punya penghubung untuk menarik data dari API luar. Menyambungkannya butuh URL endpoint sumber dan kunci API, keduanya diatur di
          konfigurasi server, bukan di halaman ini. Sampai itu tersedia, gunakan unggahan manual.
        </EmptyState>
      </Panel>

      {summary?.note ? (
        <Notice tone="info" title="Catatan dataset">
          <p>{summary.note}</p>
        </Notice>
      ) : null}

      {status.status === "loading" ? (
        <Panel title="Kesehatan data">
          <div aria-busy="true">
            <EmptyState icon="data" title="Memuat status dataset">
              Status diambil dari data industri ini.
            </EmptyState>
          </div>
        </Panel>
      ) : status.status === "error" ? (
        <Notice tone="danger" title="Status dataset tidak dapat dimuat" action={<RetryButton onClick={status.reload} />}>
          <p>{status.error}</p>
        </Notice>
      ) : (
        <>
          <HealthTiles status={status.data} />
          <IssuesPanel issues={status.data.issues} />
        </>
      )}

      {schema.status === "ready" ? <SchemaPanel schema={schema.data} /> : null}
      {status.status === "ready" ? <TablesPanel status={status.data} /> : null}
    </>
  );
}

function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="btn-secondary" onClick={onClick}>
      Coba lagi
    </button>
  );
}

function HealthTiles({ status }: { status: DatasetStatus }) {
  const rows = status.tables.reduce((sum, table) => sum + table.rows, 0);
  return (
    <StatTileRow label="Ringkasan kesehatan data">
      <StatTile label="Kelengkapan" value={share(status.completeness)} />
      <StatTile label="Tabel dimuat" value={status.tables.length} />
      <StatTile label="Baris" value={rows} />
      <StatTile label="Masalah data" value={status.issues.length} />
    </StatTileRow>
  );
}

function IssuesPanel({ issues }: { issues: DataIssue[] }) {
  const sorted = sortIssues(issues);
  return (
    <Panel
      title="Masalah data"
      description={
        sorted.length > 0
          ? `${count(sorted.length)} masalah ditemukan saat data dimuat, dari yang paling berat. Data tidak diubah; perbaiki di sumbernya lalu unggah ulang.`
          : "Hasil pemeriksaan kualitas saat data dimuat."
      }
      variant={sorted.length > 0 ? "flush" : "default"}
    >
      {sorted.length > 0 ? (
        <ListRows
          label="Masalah data, dari yang paling berat"
          items={sorted.map((issue) => ({
            id: issue.id,
            title: rawText(issue.title),
            description: rawText(issue.detail),
            meta: rawText(issueMeta(issue)),
            trailing: (
              <Badge tone={SEVERITY[issue.severity].tone} icon={SEVERITY[issue.severity].icon}>
                {SEVERITY[issue.severity].text}
              </Badge>
            ),
          }))}
        />
      ) : (
        <EmptyState icon="good" title="Tidak ada masalah terdeteksi">
          Pemeriksaan kualitas tidak menemukan masalah pada data yang sedang dimuat.
        </EmptyState>
      )}
    </Panel>
  );
}

function issueMeta(issue: DataIssue): string {
  const where = issue.column ? `Tabel ${issue.table}, kolom ${issue.column}.` : `Tabel ${issue.table}.`;
  return `${where} ${count(issue.count)} baris terdampak.`;
}

// ---------------------------------------------------------------------------
// Upload

interface DropView {
  state: FileDropState;
  message: ReactNode;
}

const IDLE: DropView = { state: "idle", message: "Belum ada berkas dipilih." };

function UploadPanel({
  slug,
  schema,
  onUploaded,
}: {
  slug: IndustrySlug;
  schema: ApiState<DatasetSchema> & { reload: () => void };
  onUploaded: () => void;
}) {
  const selectId = useId();
  const hintId = useId();
  const [table, setTable] = useState("");
  const [drop, setDrop] = useState<DropView>(IDLE);
  const [result, setResult] = useState<IngestResult | null>(null);

  if (schema.status !== "ready") {
    return (
      <Panel title="Unggah data manual" description="Ganti data industri ini dengan berkas dari komputer Anda.">
        {schema.status === "loading" ? (
          <div aria-busy="true">
            <EmptyState icon="upload" title="Memuat format berkas">
              Format yang diterima diambil dari server.
            </EmptyState>
          </div>
        ) : (
          <Notice tone="danger" title="Format berkas tidak dapat dimuat" action={<RetryButton onClick={schema.reload} />}>
            <p>{schema.error}</p>
          </Notice>
        )}
      </Panel>
    );
  }

  const spec = schema.data;
  const choose = needsTableChoice(spec);
  const limit = bytesText(spec.maxUploadBytes);
  const kinds = acceptsText(spec.accepts);

  const onFiles = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setResult(null);
    if (!matchesAccept(file.name, spec.accepts)) {
      setDrop({ state: "error", message: `${file.name} bukan berkas ${kinds}.` });
      return;
    }
    if (file.size > spec.maxUploadBytes) {
      setDrop({ state: "error", message: `${file.name} lebih besar dari batas ${limit}.` });
      return;
    }
    const single = choose && isSingleTableFile(file.name);
    if (single && !table) {
      setDrop({ state: "error", message: "Pilih tabel yang diganti untuk berkas CSV atau XLSX, lalu pilih berkasnya lagi." });
      return;
    }
    setDrop({ state: "uploading", message: `Mengunggah ${file.name}` });
    try {
      const outcome = await endpoints.upload(slug, file, single ? table : undefined);
      setResult(outcome);
      if (outcome.ok) {
        setDrop({ state: "success", message: `${outcome.filename} diterima. Status data dimuat ulang.` });
        onUploaded();
      } else {
        setDrop({ state: "error", message: `${outcome.filename} belum bisa dipakai. Rinciannya di bawah.` });
      }
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 413
          ? `${file.name} ditolak server karena terlalu besar. Batasnya ${limit}.`
          : err instanceof Error
            ? err.message
            : "Unggahan gagal.";
      setDrop({ state: "error", message });
    }
  };

  return (
    <Panel title="Unggah data manual" description={rawText(spec.howTo)}>
      {choose ? (
        <div className="field con-drop-field">
          <label className="field-label" htmlFor={selectId}>
            Tabel yang diganti
          </label>
          <select id={selectId} value={table} aria-describedby={hintId} onChange={(event) => setTable(event.target.value)}>
            <option value="">Seluruh dataset (berkas JSON)</option>
            {spec.tables.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
              </option>
            ))}
          </select>
          <p className="field-hint" id={hintId}>
            Hanya dipakai untuk berkas CSV atau XLSX. Berkas JSON selalu mengganti seluruh dataset.
          </p>
        </div>
      ) : null}
      <FileDrop
        label={choose ? "Berkas dataset atau tabel" : "Berkas data"}
        hint={`${kinds}, maksimal ${limit}.`}
        accept={acceptAttr(spec.accepts)}
        onFiles={(files) => void onFiles(files)}
        state={drop.state}
        message={drop.message}
      />
      {result ? (
        <div className="con-drop-field">
          <IngestReport result={result} />
        </div>
      ) : null}
    </Panel>
  );
}

function tableList(tables: IngestResult["tables"]): string {
  return tables.map((t) => `${t.name} (${count(t.rows)} baris)`).join(", ");
}

function IngestReport({ result }: { result: IngestResult }) {
  if (result.ok) {
    return (
      <Notice tone="success" title="Data diganti">
        {result.tables.length > 0 ? (
          <p>
            {result.filename}: {count(result.tables.length)} tabel, {count(result.tables.reduce((sum, t) => sum + t.rows, 0))} baris dimuat.
          </p>
        ) : null}
        {result.unknown.length > 0 ? <p>Diabaikan karena tidak dipakai industri ini: {rawText(result.unknown.join(", "))}.</p> : null}
      </Notice>
    );
  }
  return (
    <Notice tone="danger" title="Berkas belum bisa dipakai">
      <p>Data yang sedang dipakai tidak berubah. Perbaiki berkasnya lalu unggah lagi.</p>
      {result.missing.length > 0 ? <p>Tabel wajib yang tidak ada: {rawText(result.missing.join(", "))}.</p> : null}
      {result.unknown.length > 0 ? <p>Tabel yang tidak dipakai industri ini: {rawText(result.unknown.join(", "))}.</p> : null}
      {result.errors.map((error) => (
        <p key={error}>{error}</p>
      ))}
      {result.tables.length > 0 ? <p>Tabel yang terbaca: {rawText(tableList(result.tables))}.</p> : null}
    </Notice>
  );
}

// ---------------------------------------------------------------------------
// Format and loaded tables

type SchemaTable = DatasetSchema["tables"][number];

const SCHEMA_COLUMNS: DataColumn<SchemaTable>[] = [
  { key: "name", header: "Tabel", cell: (t) => <Ident name={t.name} />, mobile: "title" },
  { key: "grain", header: "Grain", cell: (t) => rawText(`Satu baris: ${t.grain}`), wrap: true, mobile: "meta" },
  {
    key: "required",
    header: "Wajib",
    cell: (t) => (t.required ? <Badge tone="info">Wajib</Badge> : <Badge>Opsional</Badge>),
    mobile: "trailing",
  },
  { key: "columns", header: "Kolom", cell: (t) => rawText(t.columns.join(", ")), wrap: true, mobile: "meta" },
];

function SchemaPanel({ schema }: { schema: DatasetSchema }) {
  const required = schema.tables.filter((t) => t.required).length;
  return (
    <Panel
      title="Format berkas yang diterima"
      description={`${acceptsText(schema.accepts)}, maksimal ${bytesText(schema.maxUploadBytes)}. ${count(schema.tables.length)} tabel, ${count(required)} wajib ada.`}
      variant="flush"
    >
      <DataTable caption="Tabel dan kolom yang diharapkan" columns={SCHEMA_COLUMNS} rows={schema.tables} rowKey={(t) => t.name} height="lg" />
    </Panel>
  );
}

const TABLE_COLUMNS: DataColumn<TableStatus>[] = [
  { key: "name", header: "Tabel", cell: (t) => <Ident name={t.name} />, mobile: "title" },
  { key: "grain", header: "Grain", cell: (t) => rawText(`Satu baris: ${t.grain}`), wrap: true, mobile: "meta" },
  { key: "rows", header: "Baris", cell: (t) => `${count(t.rows)} baris`, numeric: true, mobile: "meta" },
  { key: "columns", header: "Kolom", cell: (t) => count(t.columns.length), numeric: true, mobile: "hidden" },
  { key: "completeness", header: "Kelengkapan", cell: (t) => `${share(t.completeness)} lengkap`, numeric: true, mobile: "meta" },
];

function TablesPanel({ status }: { status: DatasetStatus }) {
  const groups = groupByKind(status.tables);
  return (
    <Panel
      title="Tabel yang dimuat"
      description={`Sumber ${status.source}, ${ORIGIN_TEXT[status.origin]}, dimuat ${loadedAtText(status.loadedAt)}.`}
      variant="flush"
    >
      {groups.length === 0 ? (
        <EmptyState icon="data" title="Belum ada tabel">
          Dataset ini belum memuat tabel apa pun. Unggah berkas untuk mengisinya.
        </EmptyState>
      ) : (
        groups.map((group) => (
          <div className="con-table" key={group.kind}>
            <p className="con-table-caption" role="heading" aria-level={3}>
              {KIND_TEXT[group.kind]}, {count(group.tables.length)} tabel
            </p>
            <DataTable
              caption={`${KIND_TEXT[group.kind]}, ${count(group.tables.length)} tabel`}
              columns={TABLE_COLUMNS}
              rows={group.tables}
              rowKey={(t) => t.name}
            />
          </div>
        ))
      )}
    </Panel>
  );
}
